// Words for the sign-up / sign-in / set-password screens in both site languages.
// Most CHDS customers use the Chinese site, so every message a customer can hit here —
// including the server's — must read naturally in Chinese.
import { Modal } from 'bootstrap';

const CONTACT_EN = 'message us on WeChat or call 0489 198 036';
const CONTACT_ZH = '请微信联系我们或致电 0489 198 036';

const T = {
  en: {
    // sign in
    loginTitle: 'Login', loginSub: 'Hi 👋 there! Log in and let’s make today’s meals memorable',
    idLabel: 'Email or mobile number', idPh: 'Email address or mobile number',
    pwLabel: 'Password', pwPh: 'Enter password', loginBtn: 'Login', or: 'OR',
    forgot: 'Forgot password?', noAccount: 'Don’t have an account?', signUpNow: 'Sign up now',
    idRequired: 'Enter your email or mobile number.', idInvalid: 'Please enter a valid email address or mobile number.',
    pwRequired: 'Enter your password.', invalid: 'That email/number and password don’t match. Check them, or tap “Forgot password?”.',
    // sign up
    nameReq: 'Please enter your name.', emailReq: 'Please enter your email.', emailBad: 'Please enter a valid email address.',
    phoneReq: 'Please enter your mobile number.', phoneBad: 'Please enter an Australian mobile number, e.g. 0412 345 678.',
    pwReq: 'Please choose a password.', pwConfirmReq: 'Please type your password again.', pwMismatch: 'The two passwords don’t match.',
    pwHint: 'At least 8 characters, and not only numbers.',
    pwShort: 'Your password needs at least 8 characters.', pwNumeric: 'Your password can’t be only numbers — add a letter.',
    pwCommon: 'That password is too easy to guess — try another.', pwSimilar: 'Your password is too similar to your name or email.',
    exists: 'You already have a CHDS account with this email or number. Please sign in.',
    signInInstead: 'Sign in', resetInstead: 'Forgot password?',
    needsLink: `Your number is already on file from an order you placed with us by WeChat or phone, but that account has no email or password yet. For your security our team needs to link your email — we’ve just let them know. You can also ${CONTACT_EN}, or place this order with us on WeChat as usual.`,
    unexpected: 'Something went wrong on our side. Please try again in a moment.',
    codeLimited: `You’ve asked for several codes in a short time, so we’ve paused sending for a while. Please try again in an hour, or ${CONTACT_EN}.`,
    codeFailed: `You already have a CHDS account, but we couldn’t email your code just now. Please try again in a few minutes, or ${CONTACT_EN}.`,
    network: 'We couldn’t reach CHDS. Check your internet connection and try again.',
    // set password with a code
    claimIntro: (m) => `Good news — you already have a CHDS account from a previous order. We’ve emailed a 6-digit code to ${m}. Enter it below and choose a password.`,
    claimWait: (m) => `You already have a CHDS account. We sent a 6-digit code to ${m} a moment ago — enter it below and choose a password.`,
    noPwIntro: (m) => `Your account doesn’t have a password yet. We’ve emailed a 6-digit code to ${m} — enter it below and choose one.`,
    fpTitle: 'Forgot password', fpSub: 'Enter your email or mobile number and we’ll email you a code to reset your password.',
    sendCode: 'Send code', remember: 'Remember your password?', backToLogin: 'Back to login',
    vTitle: 'Enter your code', sentTo: 'We’ve sent a 6-digit code to', timeLeft: 'Time remaining:',
    codeLabel: '6-digit code', codePh: 'Enter code', newPw: 'New password', newPwPh: 'Choose a password',
    confirmPw: 'Type it again', confirmPwPh: 'Type your password again', setPwBtn: 'Set password',
    resend: 'Send a new code', resendIn: (s) => `New code in ${s}s`, wrongId: 'Wrong email?', changeId: 'Change it',
    codeReq: 'Enter the 6-digit code from the email.', codeBad: 'The code is 6 numbers.',
    resetDone: 'Password set! Signing you in…', resentOk: 'We’ve sent a new code.',
    phoneNoEmail: `If your account was set up from a WeChat or phone order it may not have an email yet — ${CONTACT_EN} and we’ll link one so you can set a password.`,
  },
  zh: {
    loginTitle: '登录', loginSub: '您好 👋 登录后即可下单',
    idLabel: '邮箱或手机号', idPh: '请输入邮箱或手机号',
    pwLabel: '密码', pwPh: '请输入密码', loginBtn: '登录', or: '或',
    forgot: '忘记密码？', noAccount: '还没有账户？', signUpNow: '立即注册',
    idRequired: '请输入邮箱或手机号。', idInvalid: '请输入有效的邮箱地址或手机号。',
    pwRequired: '请输入密码。', invalid: '邮箱/手机号与密码不匹配。请检查后重试，或点击“忘记密码？”。',
    nameReq: '请输入您的姓名。', emailReq: '请输入邮箱。', emailBad: '请输入有效的邮箱地址。',
    phoneReq: '请输入手机号。', phoneBad: '请输入澳洲手机号，例如 0412 345 678。',
    pwReq: '请设置密码。', pwConfirmReq: '请再次输入密码。', pwMismatch: '两次输入的密码不一致。',
    pwHint: '至少 8 位，且不能全是数字。',
    pwShort: '密码至少需要 8 位。', pwNumeric: '密码不能全是数字，请加入字母。',
    pwCommon: '这个密码太常见、容易被猜到，请换一个。', pwSimilar: '密码与您的姓名或邮箱太相似，请换一个。',
    exists: '这个邮箱或手机号已经有 CHDS 账户了，请直接登录。',
    signInInstead: '去登录', resetInstead: '忘记密码？',
    needsLink: `您的手机号已在我们这里登记（您之前通过微信或电话下过单），但该账户还没有邮箱和密码。为保护您的账户安全，需要由我们的工作人员为您绑定邮箱——我们已经通知了他们。您也可以${CONTACT_ZH}，或照常在微信上向我们下单。`,
    unexpected: '系统出了点问题，请稍后再试。',
    codeLimited: `您在短时间内请求了多次验证码，系统已暂停发送。请一小时后再试，或${CONTACT_ZH}。`,
    codeFailed: `您已有 CHDS 账户，但验证码邮件暂时没能发出。请几分钟后再试，或${CONTACT_ZH}。`,
    network: '无法连接到 CHDS，请检查网络后重试。',
    claimIntro: (m) => `好消息——您之前下过单，已经有 CHDS 账户了。我们已将 6 位验证码发送到 ${m}，请在下方输入验证码并设置密码。`,
    claimWait: (m) => `您已有 CHDS 账户。我们刚刚已将 6 位验证码发送到 ${m}，请在下方输入并设置密码。`,
    noPwIntro: (m) => `您的账户还没有设置密码。我们已将 6 位验证码发送到 ${m}，请在下方输入并设置密码。`,
    fpTitle: '忘记密码', fpSub: '请输入邮箱或手机号，我们会把重置密码的验证码发到您的邮箱。',
    sendCode: '发送验证码', remember: '想起密码了？', backToLogin: '返回登录',
    vTitle: '输入验证码', sentTo: '我们已将 6 位验证码发送到', timeLeft: '剩余时间：',
    codeLabel: '6 位验证码', codePh: '请输入验证码', newPw: '新密码', newPwPh: '请设置密码',
    confirmPw: '确认密码', confirmPwPh: '请再次输入密码', setPwBtn: '设置密码',
    resend: '重新发送验证码', resendIn: (s) => `${s} 秒后可重新发送`, wrongId: '邮箱填错了？', changeId: '修改',
    codeReq: '请输入邮件中的 6 位验证码。', codeBad: '验证码是 6 位数字。',
    resetDone: '密码已设置！正在为您登录…', resentOk: '新的验证码已发送。',
    phoneNoEmail: `如果您的账户是通过微信或电话下单建立的，可能还没有绑定邮箱——${CONTACT_ZH}，我们会帮您绑定邮箱，之后即可设置密码。`,
  },
};

export function authText(lang) {
  return String(lang || '').startsWith('zh') ? T.zh : T.en;
}

// Django's password-rule codes → a sentence the customer understands.
export function passwordMessage(L, codes) {
  const map = { password_too_short: L.pwShort, password_entirely_numeric: L.pwNumeric,
    password_too_common: L.pwCommon, password_too_similar: L.pwSimilar };
  const out = [...new Set((codes || []).map((c) => map[c]).filter(Boolean))];
  return out.join(' ');
}

// Australian mobile in any spelling ('+61 412…', '61412…', '412…', '0412 …') → '0412345678'.
export function normaliseAuPhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('0061')) d = d.slice(4);
  else if (d.startsWith('61') && d.length === 11) d = d.slice(2);
  if (d.length === 9) d = '0' + d;
  return d;
}

// Jump to the "enter your code + choose a password" step, closing whichever modal is open first
// (Bootstrap misplaces the backdrop if two modals animate at once).
export function openCodeStep(detail) {
  let fired = false;
  const fire = () => { if (fired) return; fired = true; window.dispatchEvent(new CustomEvent('chds:code-step', { detail })); };
  const open = document.querySelector('.modal.show');
  if (open) {
    open.addEventListener('hidden.bs.modal', fire, { once: true });
    Modal.getOrCreateInstance(open).hide();
    setTimeout(fire, 900);   // never leave the customer without the code screen if that event is missed
  } else {
    fire();
  }
}
